import React, { useState, useEffect } from "react";
import "./EmployeeAttendance.css";

const EmployeeAttendance = () => {
  const [attendanceData, setAttendanceData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [checkInForm, setCheckInForm] = useState({
    location: "Remote",
    notes: "",
  });
  const [checkOutForm, setCheckOutForm] = useState({
    location: "Remote",
    notes: "",
  });

  useEffect(() => {
    fetchTodayAttendance();
    fetchStats();
  }, []);

  const fetchTodayAttendance = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("authToken");
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_BASEURL}/api/attendance/today`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const data = await response.json();
      if (data.success) {
        setAttendanceData(data);
      }
    } catch (error) {
      console.error("Error fetching attendance:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem("authToken");
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_BASEURL}/api/attendance/my-stats`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const data = await response.json();
      if (data.success) {
        setStats(data.stats);
      }
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };

  const handleCheckIn = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("authToken");
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_BASEURL}/api/attendance/check-in`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(checkInForm),
        }
      );

      const data = await response.json();
      if (data.success) {
        alert("Checked in successfully!");
        fetchTodayAttendance();
        setCheckInForm({ location: "Remote", notes: "" });
      } else {
        alert(data.message);
      }
    } catch (error) {
      console.error("Error checking in:", error);
      alert("Error checking in");
    } finally {
      setLoading(false);
    }
  };

  const handleCheckOut = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("authToken");
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_BASEURL}/api/attendance/check-out`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(checkOutForm),
        }
      );

      const data = await response.json();
      if (data.success) {
        alert("Checked out successfully!");
        fetchTodayAttendance();
        setCheckOutForm({ location: "Remote", notes: "" });
      } else {
        alert(data.message);
      }
    } catch (error) {
      console.error("Error checking out:", error);
      alert("Error checking out");
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (dateString) => {
    return new Date(dateString).toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatWorkingHours = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  if (loading && !attendanceData) {
    return (
      <div className="attendance-container">
        <div className="loading-state">
          <i className="fas fa-spinner fa-spin"></i>
          <p>Loading attendance...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="attendance-container">
      <div className="attendance-header">
        <h1>
          <i className="fas fa-clock"></i>
          My Attendance
        </h1>
        <p>Track your daily attendance and working hours</p>
      </div>

      {/* Today's Attendance Status */}
      <div className="today-attendance-card">
        <h2>Today's Status</h2>
        <div className="attendance-status">
          {attendanceData?.attendance ? (
            <div className="status-details">
              <div className="status-item">
                <div className="status-label">Status</div>
                <div
                  className={`status-value ${attendanceData.attendance.status}`}
                >
                  {attendanceData.attendance.status}
                </div>
              </div>

              {attendanceData.attendance.checkIn?.time && (
                <div className="status-item">
                  <div className="status-label">Check In</div>
                  <div className="status-value">
                    {formatTime(attendanceData.attendance.checkIn.time)}
                  </div>
                </div>
              )}

              {attendanceData.attendance.checkOut?.time && (
                <div className="status-item">
                  <div className="status-label">Check Out</div>
                  <div className="status-value">
                    {formatTime(attendanceData.attendance.checkOut.time)}
                  </div>
                </div>
              )}

              {attendanceData.attendance.workingHours?.total > 0 && (
                <div className="status-item">
                  <div className="status-label">Working Hours</div>
                  <div className="status-value">
                    {formatWorkingHours(
                      attendanceData.attendance.workingHours.total
                    )}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="no-attendance">
              <i className="fas fa-calendar-times"></i>
              <p>No attendance recorded for today</p>
            </div>
          )}
        </div>
      </div>

      {/* Check In/Out Buttons */}
      <div className="attendance-actions">
        {attendanceData?.canCheckIn && (
          <div className="check-in-section">
            <h3>Check In</h3>
            <div className="form-group">
              <label>Location</label>
              <select
                value={checkInForm.location}
                onChange={(e) =>
                  setCheckInForm({ ...checkInForm, location: e.target.value })
                }
              >
                <option value="Remote">Remote</option>
                <option value="Office">Office</option>
                <option value="Client Site">Client Site</option>
              </select>
            </div>
            <div className="form-group">
              <label>Notes</label>
              <textarea
                value={checkInForm.notes}
                onChange={(e) =>
                  setCheckInForm({ ...checkInForm, notes: e.target.value })
                }
                placeholder="Any additional notes..."
                rows="3"
              />
            </div>
            <button
              className="btn btn-check-in"
              onClick={handleCheckIn}
              disabled={loading}
            >
              <i className="fas fa-sign-in-alt"></i>
              {loading ? "Checking In..." : "Check In"}
            </button>
          </div>
        )}

        {attendanceData?.canCheckOut && (
          <div className="check-out-section">
            <h3>Check Out</h3>
            <div className="form-group">
              <label>Location</label>
              <select
                value={checkOutForm.location}
                onChange={(e) =>
                  setCheckOutForm({ ...checkOutForm, location: e.target.value })
                }
              >
                <option value="Remote">Remote</option>
                <option value="Office">Office</option>
                <option value="Client Site">Client Site</option>
              </select>
            </div>
            <div className="form-group">
              <label>Notes</label>
              <textarea
                value={checkOutForm.notes}
                onChange={(e) =>
                  setCheckOutForm({ ...checkOutForm, notes: e.target.value })
                }
                placeholder="Any additional notes..."
                rows="3"
              />
            </div>
            <button
              className="btn btn-check-out"
              onClick={handleCheckOut}
              disabled={loading}
            >
              <i className="fas fa-sign-out-alt"></i>
              {loading ? "Checking Out..." : "Check Out"}
            </button>
          </div>
        )}

        {!attendanceData?.canCheckIn && !attendanceData?.canCheckOut && (
          <div className="time-restrictions">
            <h3>Time Restrictions</h3>
            <div className="restriction-info">
              <div className="restriction-item">
                <i className="fas fa-clock"></i>
                <span>
                  Check-in: {attendanceData?.restrictions?.checkInWindow}
                </span>
              </div>
              <div className="restriction-item">
                <i className="fas fa-clock"></i>
                <span>
                  Check-out: {attendanceData?.restrictions?.checkOutAfter}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Attendance Statistics */}
      {stats && (
        <div className="attendance-stats">
          <h2>This Month's Statistics</h2>
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-icon">
                <i className="fas fa-calendar-check"></i>
              </div>
              <div className="stat-info">
                <div className="stat-value">{stats.presentDays || 0}</div>
                <div className="stat-label">Present Days</div>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon">
                <i className="fas fa-calendar-times"></i>
              </div>
              <div className="stat-info">
                <div className="stat-value">{stats.absentDays || 0}</div>
                <div className="stat-label">Absent Days</div>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon">
                <i className="fas fa-clock"></i>
              </div>
              <div className="stat-info">
                <div className="stat-value">{stats.lateDays || 0}</div>
                <div className="stat-label">Late Days</div>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon">
                <i className="fas fa-percentage"></i>
              </div>
              <div className="stat-info">
                <div className="stat-value">
                  {stats.attendancePercentage || 0}%
                </div>
                <div className="stat-label">Attendance %</div>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon">
                <i className="fas fa-hourglass-half"></i>
              </div>
              <div className="stat-info">
                <div className="stat-value">
                  {stats.totalWorkingHours || 0}h
                </div>
                <div className="stat-label">Total Hours</div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeeAttendance;
//asd
